import _ from 'lodash';

/**
 * Determines whether a conversation is missing required topics/custom attributes
 * based on the conversation workflow config.
 *
 * When topicRequirements is present, client-side validation is best-effort:
 * - Unconstrained topic groups: checks total topic count
 * - Group-specific requirements: verifies ancestry when the topics list is available, else falls
 *   back to a count check (the server validates definitively)
 * - Custom attribute requirements: checks each required attribute is present
 */
export function computeTopicRequirementMessage(config, conversation, topics, customAttributes) {
  if (!config.requireTopics) {
    return '';
  }

  const { topicRequirements } = config;
  const { topicGroups, customAttributeGroups } = topicRequirements || {};
  const hasEnhancedRequirements = !_.isEmpty(topicGroups) || !_.isEmpty(customAttributeGroups);

  if (!hasEnhancedRequirements) {
    return 'Apply a topic before closing the conversation';
  }

  const items = [];
  const unmetGroups = computeUnmetTopicGroups(topicGroups || [], _.uniq(conversation.topicIds), topics);

  const unmetByParent = _.groupBy(
    unmetGroups.filter(g => g.parentTopicId),
    'parentTopicId'
  );
  Object.entries(unmetByParent).forEach(([parentId, groups]) => {
    const name = _.get(_.find(topics, { id: parentId }), 'name');
    if (!name) return;
    const n = groups.length;
    items.push(n === 1 ? `A topic from '${name}'` : `${n} topics from '${name}'`);
  });

  const anyTopicUnmetCount = unmetGroups.filter(g => !g.parentTopicId).length;
  if (anyTopicUnmetCount > 0) {
    items.push(anyTopicUnmetCount === 1 ? 'Any topic' : `Any ${anyTopicUnmetCount} topics`);
  }

  if (!_.isEmpty(customAttributeGroups)) {
    const appliedIds = new Set(_.map(conversation.customAttributes, 'id'));
    const missingAttrNames = customAttributeGroups
      .filter(req => !appliedIds.has(req.customAttributeId))
      .map(req => _.get(_.find(customAttributes, { id: req.customAttributeId }), 'label'))
      .filter(Boolean);

    if (missingAttrNames.length) {
      const quoted = missingAttrNames.map(name => `'${name}'`);
      const list =
        quoted.length === 1 ? quoted[0] : `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
      items.push(missingAttrNames.length === 1 ? `A value for ${list}` : `Values for ${list}`);
    }
  }

  if (!items.length) {
    return 'Apply topics before closing the conversation';
  }

  if (items.length === 1) {
    return `Apply ${lowercaseFirst(items[0])} before closing the conversation`;
  }

  return `Please apply before closing:\n${items.map(i => `• ${i}`).join('\n')}`;
}

function lowercaseFirst(str) {
  return `${str.charAt(0).toLowerCase()}${str.slice(1)}`;
}

/**
 * Returns the topicGroups that cannot be matched to any applied topic.
 * Uses bipartite matching: each applied topic can satisfy at most one group.
 * A constrained group (parentTopicId set) is satisfied by a topic that is the
 * parent itself or one of its descendants. An unconstrained group is satisfied
 * by any applied topic.
 */
export function computeUnmetTopicGroups(topicGroups, appliedTopicIds, topics) {
  const topicsById = _.keyBy(topics, 'id');
  // Belt-and-suspenders: the UI prevents applying category-only topics, but if
  // one ever leaks through (legacy data, race, non-UI client) it must not falsely
  // satisfy a requirement — the server would reject the close and confuse the agent.
  const eligibleIds = appliedTopicIds.filter(id => !_.get(topicsById[id], 'categoryOnly'));
  const satisfyingSets = topicGroups.map(group => {
    if (!group.parentTopicId) {
      return new Set(eligibleIds);
    }
    const parent = topicsById[group.parentTopicId];
    const allowed = new Set([group.parentTopicId, ..._.get(parent, 'descendants', [])]);
    return new Set(eligibleIds.filter(id => allowed.has(id)));
  });

  const matchedTopic = new Array(topicGroups.length).fill(null);
  const tryMatch = (groupIndex, usedTopics) => {
    for (const topicId of satisfyingSets[groupIndex]) {
      if (usedTopics.has(topicId)) continue;
      usedTopics.add(topicId);
      const prevGroup = _.findIndex(matchedTopic, t => t === topicId);
      if (prevGroup === -1 || tryMatch(prevGroup, usedTopics)) {
        matchedTopic[groupIndex] = topicId;
        return true;
      }
    }
    return false;
  };

  // Match most-constrained groups first; tiebreak by preferring constrained over any-topic.
  const order = _.sortBy(_.range(topicGroups.length), i => [
    satisfyingSets[i].size,
    topicGroups[i].parentTopicId ? 0 : 1,
  ]);
  order.forEach(i => tryMatch(i, new Set()));

  return topicGroups.filter((_g, i) => matchedTopic[i] === null);
}

export function computeNeedsTopics(config, conversation, { topics = [] } = {}) {
  if (!config.requireTopics) {
    return false;
  }

  const { topicRequirements } = config;
  const { topicGroups, customAttributeGroups } = topicRequirements || {};
  const hasEnhancedRequirements = !_.isEmpty(topicGroups) || !_.isEmpty(customAttributeGroups);

  if (!hasEnhancedRequirements) {
    return !conversation.topicIds.length && !conversation.customAttributes.length;
  }

  if (!_.isEmpty(topicGroups)) {
    const appliedTopicIds = _.uniq(conversation.topicIds);
    if (appliedTopicIds.length < topicGroups.length) {
      return true;
    }
    // When topics are available, verify constrained groups are actually satisfied
    // by ancestry, not just count. Server validates definitively.
    if (!_.isEmpty(topics) && !_.isEmpty(computeUnmetTopicGroups(topicGroups, appliedTopicIds, topics))) {
      return true;
    }
  }

  if (!_.isEmpty(customAttributeGroups)) {
    const attributeIds = new Set(_.map(conversation.customAttributes, 'id'));
    const missingAttribute = customAttributeGroups.some(req => !attributeIds.has(req.customAttributeId));
    if (missingAttribute) {
      return true;
    }
  }

  return false;
}
